import {gql} from '@apollo/client';

/**
 * Generate a plan from a natural language prompt using LangGraph.
 * This creates phases, steps, and automation actions.
 */
export const GENERATE_PLAN_FROM_PROMPT = gql`
  mutation GeneratePlanFromPrompt($input: GeneratePlanInput!) {
    generatePlanFromPrompt(input: $input) {
      id
      userId
      goal
      summary
      status
      createdAt
      updatedAt
      phases {
        id
        title
        description
        order
      }
      steps {
        id
        title
        description
        phaseId
        kind
        status
        dependsOn
      }
      evaluation {
        totalSteps
        completedSteps
        percentComplete
      }
      artifacts {
        id
        fileName
        mimeType
        sizeBytes
        category
        downloadUrl
      }
    }
  }
`;

// Legacy mutation - keeping for backwards compatibility
export const CREATE_PLAN = gql`
  mutation CreatePlan($input: CreatePlanInput!) {
    createPlan(input: $input) {
      id
      userId
      title
      rawPrompt
      status
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_PLAN = gql`
  mutation DeletePlan($id: ID!) {
    deletePlanapusPlan(id: $id)
  }
`;

// ============================================================================
// AI Assistance Mutations (Phase 6)
// ============================================================================

/**
 * Update step consent level
 */
export const UPDATE_STEP_CONSENT_LEVEL = gql`
  mutation UpdateStepConsentLevel($planId: ID!, $stepId: ID!, $consentLevel: ConsentLevel!) {
    updateStepConsentLevel(planId: $planId, stepId: $stepId, consentLevel: $consentLevel) {
      id
      steps {
        id
        consentLevel
      }
    }
  }
`;

/**
 * Add guardrail to step
 */
export const ADD_STEP_GUARDRAIL = gql`
  mutation AddStepGuardrail($planId: ID!, $stepId: ID!, $guardrail: GuardrailInput!) {
    addStepGuardrail(planId: $planId, stepId: $stepId, guardrail: $guardrail) {
      id
      steps {
        id
        guardrails {
          id
          type
          config
          failureAction
        }
      }
    }
  }
`;

/**
 * Remove guardrail from step
 */
export const REMOVE_STEP_GUARDRAIL = gql`
  mutation RemoveStepGuardrail($planId: ID!, $stepId: ID!, $guardrailId: ID!) {
    removeStepGuardrail(planId: $planId, stepId: $stepId, guardrailId: $guardrailId) {
      id
      steps {
        id
        guardrails {
          id
          type
          config
          failureAction
        }
      }
    }
  }
`;

// ============================================================================
// Auto-Execution Mutations
// ============================================================================

/**
 * Execute a single step's automation actions/skills.
 * Runs the step's skills or actions and returns execution results.
 */
export const EXECUTE_STEP = gql`
  mutation ExecuteStep($planId: ID!, $stepId: ID!) {
    executeStep(planId: $planId, stepId: $stepId) {
      planId
      stepId
      status
      results {
        actionId
        status
        startedAt
        completedAt
        durationMs
        output
        error {
          message
          code
        }
      }
      errors
    }
  }
`;

/**
 * Execute all eligible automation steps in a plan.
 * Steps are eligible if they are AUTO kind, NOT_STARTED status,
 * have all dependencies met, and pass guardrail checks.
 */
export const EXECUTE_ELIGIBLE_STEPS = gql`
  mutation ExecuteEligibleSteps($planId: ID!) {
    executeEligibleSteps(planId: $planId) {
      executed
      skipped
      errors {
        stepId
        message
        code
      }
    }
  }
`;

// ============================================================================
// Step Expansion Mutations
// ============================================================================

/**
 * Expand a step into its own subplan.
 * Creates a new Plan that breaks down the step into detailed sub-steps.
 * The parent step's expansionPlanId is set to the new plan's ID.
 */
export const EXPAND_STEP_INTO_PLAN = gql`
  mutation ExpandStepIntoPlan($input: ExpandStepInput!) {
    expandStepIntoPlan(input: $input) {
      jobId
      status
      resultRef
      deduplicated
    }
  }
`;

// ============================================================================
// Background Job Mutations
// ============================================================================

/**
 * Enqueue a step for background execution.
 * Returns immediately with a job ID for tracking progress.
 */
export const ENQUEUE_STEP_EXECUTION = gql`
  mutation EnqueueStepExecution($planId: ID!, $stepId: ID!, $cascade: Boolean) {
    enqueueStepExecution(planId: $planId, stepId: $stepId, cascade: $cascade) {
      jobId
      jobType
      state
      message
    }
  }
`;

/**
 * Submit an approval decision for a step requiring approval.
 * If approved, triggers automation execution.
 */
export const SUBMIT_STEP_APPROVAL = gql`
  mutation SubmitStepApproval($planId: ID!, $stepId: ID!, $decision: ApprovalDecision!, $comment: String) {
    submitStepApproval(planId: $planId, stepId: $stepId, decision: $decision, comment: $comment) {
      id
      steps {
        id
        status
        approvalRecords {
          id
          approverUserId
          decision
          comment
          decidedAt
        }
      }
    }
  }
`;

/**
 * Enqueue all eligible steps for background execution.
 * Returns immediately with a job ID for tracking progress.
 */
export const ENQUEUE_ELIGIBLE_STEPS_EXECUTION = gql`
  mutation EnqueueEligibleStepsExecution($planId: ID!) {
    enqueueEligibleStepsExecution(planId: $planId) {
      jobId
      jobType
      state
      message
    }
  }
`;

/**
 * Get the status of a background job.
 */
// ============================================================================
// Guide Mutations
// ============================================================================

/**
 * Delete a guide permanently.
 */
export const DELETE_GUIDE = gql`
  mutation DeleteGuide($guideId: ID!) {
    deleteGuide(guideId: $guideId)
  }
`;

/**
 * Save a guide draft version with prompt and attachment IDs.
 */
export const SAVE_GUIDE_DRAFT = gql`
  mutation SaveGuideDraft($guideId: ID!, $prompt: String!, $attachmentIds: [String!]) {
    saveGuideDraft(guideId: $guideId, prompt: $prompt, attachmentIds: $attachmentIds) {
      id
      guideId
      versionNumber
      status
      generationPrompt
      generationFileIds
      generationStatus
      generationError
      createdAt
    }
  }
`;

/**
 * Regenerate the draft version output using the AI pipeline.
 */
export const REGENERATE_GUIDE_DRAFT = gql`
  mutation RegenerateGuideDraft($guideId: ID!, $versionId: ID!) {
    regenerateGuideDraft(guideId: $guideId, versionId: $versionId) {
      id
      guideId
      versionNumber
      status
      generationPrompt
      generationFileIds
      generationSourceId
      generatedOutput {
        title
        description
        hero
        deadlines { label date description }
        faqBlocks { question answer }
        steps { id guideId order title summary }
        templatePlanId
        applicantRoleId
        starterRoleId
      }
      generationStatus
      generationError
      createdAt
    }
  }
`;

/**
 * Publish a completed draft version, replacing the current published version.
 */
export const PUBLISH_GUIDE_DRAFT = gql`
  mutation PublishGuideDraft($guideId: ID!, $versionId: ID!) {
    publishGuideDraft(guideId: $guideId, versionId: $versionId) {
      id
      orgSlug
      slug
      title
      description
      publishedAt
      hero
      deadlines { label date description }
      faqBlocks { question answer }
      steps { id guideId order title summary }
      templatePlanId
      applicantRoleId
      starterRoleId
      generationSourceId
    }
  }
`;

/**
 * Publish a guide (sets publishedAt to now).
 */
export const PUBLISH_GUIDE = gql`
  mutation PublishGuide($guideId: ID!) {
    publishGuide(guideId: $guideId) {
      id
      publishedAt
    }
  }
`;

/**
 * Unpublish a guide (clears publishedAt).
 */
export const UNPUBLISH_GUIDE = gql`
  mutation UnpublishGuide($guideId: ID!) {
    unpublishGuide(guideId: $guideId) {
      id
      publishedAt
    }
  }
`;

export const SET_GUIDE_THEME = gql`
  mutation SetGuideTheme($guideId: ID!, $input: GuideThemeInput!) {
    setGuideTheme(guideId: $guideId, input: $input) {
      id
      orgId
      orgSlug
      slug
      title
      description
      publishedAt
      hero
      deadlines {
        label
        date
        description
      }
      faqBlocks {
        question
        answer
      }
      steps {
        id
        guideId
        order
        title
        summary
      }
      templatePlanId
      applicantRoleId
      starterRoleId
      generationSourceId
      theme {
        brandProfileId
        brandMode
        version
        accessibilityAdjusted
        resolvedFromOrgId
        overrides {
          organizationDisplayName
          logoUrl
          primaryColor
          secondaryColor
          tone
        }
      }
      resolvedTheme {
        organizationDisplayName
        logoUrl
        primaryColor
        secondaryColor
        backgroundColor
        textColor
        ctaTextColor
        tone
        source
        profileId
        confidenceScore
        accessibilityAdjusted
        warnings
      }
    }
  }
`;

// ============================================================================
// Organization Mutations
// ============================================================================

export const CREATE_ORGANIZATION = gql`
  mutation CreateOrganization($name: String!, $website: String) {
    createOrganization(name: $name, website: $website) {
      id
      name
      slug
      website
      createdAt
    }
  }
`;

export const SET_ACTIVE_ORGANIZATION = gql`
  mutation SetActiveOrganization($orgId: ID!) {
    setActiveOrganization(orgId: $orgId) {
      organization {
        id
        name
        slug
        createdAt
      }
    }
  }
`;

export const DISCOVER_BRAND_FROM_URL = gql`
  mutation DiscoverBrandFromUrl($input: BrandDiscoveryInput!) {
    discoverBrandFromUrl(input: $input) {
      status
      confidence
      warnings
      sourceArtifacts
      discoveredAt
      brandCandidates {
        tokenType
        value
        sourceRef
        confidence
        rationale
        dedupeHash
      }
      selectedCandidates {
        logoUrl
        headerLogoUrl
        primaryColor
        secondaryColor
        organizationDisplayName
        tone
      }
    }
  }
`;

export const CREATE_ORGANIZATION_BRAND_PROFILE = gql`
  mutation CreateOrganizationBrandProfile($input: CreateOrganizationBrandProfileInput!) {
    createOrganizationBrandProfile(input: $input) {
      id
      orgId
      name
      status
      isDefault
      confidenceScore
      organizationDisplayName
      logoUrl
      headerLogoUrl
      palette {
        primary
        secondary
        background
        surface
        text
      }
      tone
      createdFromImportId
      importedAssetRefs
      provenance
      approvedBy
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_ORGANIZATION_BRAND_PROFILE = gql`
  mutation UpdateOrganizationBrandProfile($input: UpdateOrganizationBrandProfileInput!) {
    updateOrganizationBrandProfile(input: $input) {
      id
      orgId
      name
      status
      isDefault
      confidenceScore
      organizationDisplayName
      logoUrl
      headerLogoUrl
      palette {
        primary
        secondary
        background
        surface
        text
      }
      tone
      createdFromImportId
      importedAssetRefs
      provenance
      approvedBy
      createdAt
      updatedAt
    }
  }
`;

export const SET_ORGANIZATION_DEFAULT_BRAND_PROFILE = gql`
  mutation SetOrganizationDefaultBrandProfile($profileId: ID!) {
    setOrganizationDefaultBrandProfile(profileId: $profileId) {
      id
      orgId
      isDefault
      status
    }
  }
`;

export const PROMOTE_ORGANIZATION_BRAND_PROFILE = gql`
  mutation PromoteOrganizationBrandProfile($input: PromoteOrganizationBrandProfileInput!) {
    promoteOrganizationBrandProfile(input: $input) {
      id
      orgId
      name
      status
      isDefault
      confidenceScore
      organizationDisplayName
      logoUrl
      palette {
        primary
        secondary
        background
        surface
        text
      }
      tone
      createdFromImportId
      importedAssetRefs
      provenance
      approvedBy
      createdAt
      updatedAt
    }
  }
`;

// ============================================================================
// Job Mutations
// ============================================================================

export const START_ANALYSIS = gql`
  mutation StartAnalysis($prompt: String!, $attachmentIds: [String!]) {
    startAnalysis(prompt: $prompt, attachmentIds: $attachmentIds) {
      jobId
      status
      resultRef
      deduplicated
    }
  }
`;

export const START_GUIDE_GENERATION = gql`
  mutation StartGuideGeneration($prompt: String!, $orgSlug: String!, $planInput: GeneratePlanInput, $attachmentIds: [String!]) {
    startGuideGeneration(prompt: $prompt, orgSlug: $orgSlug, planInput: $planInput, attachmentIds: $attachmentIds) {
      jobId
      status
      resultRef
      deduplicated
    }
  }
`;

export const GET_JOB_STATUS = gql`
  query GetJobStatus($jobId: ID!) {
    jobStatus(jobId: $jobId) {
      jobId
      jobType
      state
      progress
      timestamp
      processedOn
      finishedOn
      result
      failedReason
      attemptsMade
    }
  }
`;

// ============================================================================
// Observability Queries & Mutations
// ============================================================================

/**
 * Get automation queue health metrics.
 */
export const GET_QUEUE_HEALTH = gql`
  query GetQueueHealth {
    automationQueueHealth {
      waiting
      active
      completed
      failed
      delayed
      deadLetter
    }
  }
`;

/**
 * Get active automation alerts.
 */
export const GET_AUTOMATION_ALERTS = gql`
  query GetAutomationAlerts($limit: Int) {
    automationAlerts(limit: $limit) {
      id
      category
      severity
      title
      message
      planId
      stepId
      jobId
      createdAt
      acknowledged
    }
  }
`;

/**
 * Get alerts for a specific plan.
 */
export const GET_PLAN_ALERTS = gql`
  query GetPlanAlerts($planId: ID!, $limit: Int) {
    planAlerts(planId: $planId, limit: $limit) {
      id
      category
      severity
      title
      message
      stepId
      createdAt
      acknowledged
    }
  }
`;

/**
 * Get the event log for a plan.
 */
export const GET_PLAN_EVENT_LOG = gql`
  query GetPlanEventLog($planId: ID!, $limit: Int) {
    planEventLog(planId: $planId, limit: $limit) {
      id
      type
      planId
      stepId
      timestamp
      data
    }
  }
`;

/**
 * Acknowledge an automation alert.
 */
export const ACKNOWLEDGE_ALERT = gql`
  mutation AcknowledgeAlert($alertId: ID!) {
    acknowledgeAlert(alertId: $alertId)
  }
`;

/**
 * Retry a permanently failed (dead letter) job.
 */
export const RETRY_DEAD_LETTER_JOB = gql`
  mutation RetryDeadLetterJob($jobId: ID!) {
    retryDeadLetterJob(jobId: $jobId) {
      jobId
      jobType
      state
      progress
      timestamp
      processedOn
      finishedOn
      result
      failedReason
      attemptsMade
    }
  }
`;

// ============================================================================
// Job Retry
// ============================================================================

export const RETRY_JOB = gql`
  mutation RetryJob($jobId: ID!) {
    retryJob(jobId: $jobId) {
      jobId
      status
      deduplicated
    }
  }
`;

// ============================================================================
// Job Cancel
// ============================================================================

export const CANCEL_JOB = gql`
  mutation CancelJob($jobId: ID!) {
    cancelJob(jobId: $jobId) {
      jobId
      status
      deduplicated
    }
  }
`;

// ============================================================================
// Document Groups
// ============================================================================

export const CREATE_DOCUMENT_GROUP = gql`
  mutation CreateDocumentGroup($orgId: ID!, $name: String!, $description: String, $groupType: GroupType!) {
    createDocumentGroup(orgId: $orgId, name: $name, description: $description, groupType: $groupType) {
      id
      name
      description
      groupType
      version
      versionHash
      createdAt
    }
  }
`;

export const UPDATE_DOCUMENT_GROUP = gql`
  mutation UpdateDocumentGroup($id: ID!, $name: String, $description: String) {
    updateDocumentGroup(id: $id, name: $name, description: $description) {
      id
      name
      description
      updatedAt
    }
  }
`;

export const DELETE_DOCUMENT_GROUP = gql`
  mutation DeleteDocumentGroup($id: ID!) {
    deleteDocumentGroup(id: $id)
  }
`;

export const ADD_GROUP_MEMBER = gql`
  mutation AddGroupMember($groupId: ID!, $sourceItemId: ID!, $assignedBy: String!) {
    addGroupMember(groupId: $groupId, sourceItemId: $sourceItemId, assignedBy: $assignedBy) {
      id
      groupId
      sourceItemId
      assignedBy
      createdAt
    }
  }
`;

export const REMOVE_GROUP_MEMBER = gql`
  mutation RemoveGroupMember($membershipId: ID!) {
    removeGroupMember(membershipId: $membershipId)
  }
`;

export const GENERATE_GUIDE_FROM_GROUP = gql`
  mutation GenerateGuideFromGroup($groupId: ID!) {
    generateGuideFromGroup(groupId: $groupId) {
      guide {
        id
        title
      }
      generationJobId
      existingGuide
    }
  }
`;

export const COMMIT_UPLOADS_TO_SOURCE_ITEMS = gql`
  mutation CommitUploadsToSourceItems($orgId: ID!, $tempUploadIds: [ID!]!) {
    commitUploadsToSourceItems(orgId: $orgId, tempUploadIds: $tempUploadIds) {
      id
      orgId
      title
      type
      mimeType
      sizeBytes
      extractionStatus
      reviewStatus
      createdAt
      updatedAt
    }
  }
`;

export const ADD_SOURCE_ITEMS_TO_GROUP = gql`
  mutation AddSourceItemsToGroup($groupId: ID!, $sourceItemIds: [ID!]!, $assignedBy: String!) {
    addSourceItemsToGroup(groupId: $groupId, sourceItemIds: $sourceItemIds, assignedBy: $assignedBy) {
      id
      groupId
      sourceItemId
      assignedBy
      createdAt
    }
  }
`;

// ============================================================================
// Group Suggestions
// ============================================================================

export const GENERATE_GROUP_SUGGESTIONS = gql`
  mutation GenerateGroupSuggestions($orgId: ID!) {
    generateGroupSuggestions(orgId: $orgId) {
      id
      type
      status
      currentStage
      createdAt
    }
  }
`;

export const ACCEPT_GROUP_SUGGESTION = gql`
  mutation AcceptGroupSuggestion($suggestionId: ID!) {
    acceptGroupSuggestion(suggestionId: $suggestionId) {
      id
      name
      description
      groupType
      createdAt
    }
  }
`;

export const DISCARD_GROUP_SUGGESTION = gql`
  mutation DiscardGroupSuggestion($suggestionId: ID!) {
    discardGroupSuggestion(suggestionId: $suggestionId)
  }
`;

export const EXTRACT_SOURCE_ITEMS = gql`
  mutation ExtractSourceItems($orgId: ID!, $sourceItemIds: [ID!]!) {
    extractSourceItems(orgId: $orgId, sourceItemIds: $sourceItemIds) {
      id
      type
      status
      currentStage
      createdAt
    }
  }
`;

export const CLASSIFY_SOURCE_ITEMS = gql`
  mutation ClassifySourceItems($orgId: ID!, $sourceItemIds: [ID!]!) {
    classifySourceItems(orgId: $orgId, sourceItemIds: $sourceItemIds) {
      id
      type
      status
      currentStage
      createdAt
    }
  }
`;

export const OVERRIDE_SOURCE_ITEM_CLASSIFICATION = gql`
  mutation OverrideSourceItemClassification($id: ID!, $isProcess: Boolean!) {
    overrideSourceItemClassification(id: $id, isProcess: $isProcess) {
      id
      classificationStatus
      classificationOverride {
        isProcess
        setBy
        setAt
      }
    }
  }
`;

export const SCAN_LIBRARY = gql`
  mutation ScanLibrary($orgId: ID!, $tasks: [String!], $resetStatuses: Boolean, $resetTasks: [String!], $sourceItemIds: [ID!], $sampleSize: Int, $promptName: String, $modelName: String) {
    scanLibrary(orgId: $orgId, tasks: $tasks, resetStatuses: $resetStatuses, resetTasks: $resetTasks, sourceItemIds: $sourceItemIds, sampleSize: $sampleSize, promptName: $promptName, modelName: $modelName) {
      id
      type
      status
      currentStage
      createdAt
    }
  }
`;

export const DELETE_SOURCE_ITEM = gql`
  mutation DeleteSourceItem($id: ID!) {
    deleteSourceItem(id: $id)
  }
`;

export const DELETE_SOURCE_ITEMS = gql`
  mutation DeleteSourceItems($ids: [ID!]!) {
    deleteSourceItems(ids: $ids) {
      deletedIds
      blockedItems {
        id
        title
        reason
      }
    }
  }
`;

export const REGENERATE_GUIDE_FROM_GROUP = gql`
  mutation RegenerateGuideFromGroup($guideId: ID!) {
    regenerateGuideFromGroup(guideId: $guideId) {
      guide {
        id
        title
      }
      generationJobId
      existingGuide
    }
  }
`;

// ============================================================================
// Web Crawl Mutations
// ============================================================================

export const START_WEB_CRAWL = gql`
  mutation StartWebCrawl($orgId: ID!, $seedUrl: String!, $maxDepth: Int, $maxPages: Int, $skipExistingUrls: Boolean, $skipClassification: Boolean) {
    startWebCrawl(orgId: $orgId, seedUrl: $seedUrl, maxDepth: $maxDepth, maxPages: $maxPages, skipExistingUrls: $skipExistingUrls, skipClassification: $skipClassification) {
      jobId
      status
      resultRef
      deduplicated
    }
  }
`;

// ============================================================================
// Eval Mutations
// ============================================================================

export const ADD_EVAL_TEST_CASE = gql`
  mutation AddEvalTestCase($orgId: ID!, $sourceItemId: ID!, $expected: EvalExpectedInput!) {
    addEvalTestCase(orgId: $orgId, sourceItemId: $sourceItemId, expected: $expected)
  }
`;

export const DELETE_EVAL_REPORT = gql`
  mutation DeleteEvalReport($id: ID!) {
    deleteEvalReport(id: $id)
  }
`;

export const DELETE_EVAL_TEST_CASE = gql`
  mutation DeleteEvalTestCase($id: ID!) {
    deleteEvalTestCase(id: $id)
  }
`;

export const RUN_EVAL = gql`
  mutation RunEval($promptName: String, $modelName: String) {
    runEval(promptName: $promptName, modelName: $modelName) {
      id
      type
      status
      currentStage
      createdAt
    }
  }
`;

export const ITERATE_EVAL = gql`
  mutation IterateEval($promptName: String, $evalModelName: String!, $generationModelNames: [String!]!, $iterations: Int!) {
    iterateEval(promptName: $promptName, evalModelName: $evalModelName, generationModelNames: $generationModelNames, iterations: $iterations) {
      id
      type
      status
      currentStage
      createdAt
    }
  }
`;

export const SAVE_EVAL_PROMPT = gql`
  mutation SaveEvalPrompt($name: String!, $prompt: String!, $displayName: String, $origin: String, $parentName: String) {
    saveEvalPrompt(name: $name, prompt: $prompt, displayName: $displayName, origin: $origin, parentName: $parentName) {
      name
      prompt
      createdAt
      displayName
      origin
      parentName
    }
  }
`;

export const RENAME_EVAL_PROMPT = gql`
  mutation RenameEvalPrompt($oldName: String!, $newName: String!) {
    renameEvalPrompt(oldName: $oldName, newName: $newName) {
      name
      prompt
      createdAt
      displayName
      origin
      parentName
    }
  }
`;

export const DELETE_EVAL_PROMPT = gql`
  mutation DeleteEvalPrompt($name: String!) {
    deleteEvalPrompt(name: $name)
  }
`;

export const RUN_MULTI_MODEL_EVAL = gql`
  mutation RunMultiModelEval($promptName: String, $modelNames: [String!]!) {
    runMultiModelEval(promptName: $promptName, modelNames: $modelNames) {
      id
      type
      status
      currentStage
      createdAt
    }
  }
`;

export const RERUN_EVAL_CASE = gql`
  mutation RerunEvalCase($reportId: String!, $testCaseId: String!) {
    rerunEvalCase(reportId: $reportId, testCaseId: $testCaseId) {
      id
      passed
      failed
      totalCases
      guideableScore
      overallScore
      weightedScore
      fieldAccuracy {
        guideable { correct total }
        topic { correct total }
        audience { correct total }
      }
      results {
        testCaseId
        sourceItemTitle
        pass
        expected { guideable topic audience }
        actual { guideable topic audience }
        diffs
      }
    }
  }
`;

export const RERUN_EVAL_REPORT = gql`
  mutation RerunEvalReport($reportId: String!) {
    rerunEvalReport(reportId: $reportId) {
      id
      type
      status
      currentStage
      createdAt
    }
  }
`;

export const RECALCULATE_EVAL_REPORTS = gql`
  mutation RecalculateEvalReports {
    recalculateEvalReports
  }
`;

// ============================================================================
// Preview Outline
// ============================================================================

export const GENERATE_PREVIEW_OUTLINE = gql`
  mutation GeneratePreviewOutline($prompt: String!) {
    generatePreviewOutline(prompt: $prompt) {
      id
      status
    }
  }
`;

export const CLAIM_PREVIEW_PLAN = gql`
  mutation ClaimPreviewPlan($previewPlanId: ID!, $email: String!) {
    claimPreviewPlan(previewPlanId: $previewPlanId, email: $email) {
      success
      message
      generationJobId
      magicLinkUrl
    }
  }
`;

// ============================================================================
// Integration Connections
// ============================================================================

export const CREATE_CONNECT_SESSION = gql`
  mutation CreateConnectSession(
    $integrationId: String!
    $tier: ConnectionTier!
    $runId: String
    $orgId: String
  ) {
    createConnectSession(
      integrationId: $integrationId
      tier: $tier
      runId: $runId
      orgId: $orgId
    ) {
      token
      connectUrl
      expiresAt
    }
  }
`;

export const REGISTER_CONNECTION = gql`
  mutation RegisterConnection(
    $integrationId: String!
    $tier: ConnectionTier!
    $composioConnectedAccountId: String!
    $provider: String!
    $orgId: String
    $runId: String
  ) {
    registerConnection(
      integrationId: $integrationId
      tier: $tier
      composioConnectedAccountId: $composioConnectedAccountId
      provider: $provider
      orgId: $orgId
      runId: $runId
    ) {
      id
      tier
      integrationId
      provider
      status
      orgId
      principalId
      runId
      createdAt
      updatedAt
    }
  }
`;

export const REVOKE_CONNECTION = gql`
  mutation RevokeConnection($id: ID!) {
    revokeConnection(id: $id)
  }
`;

export const PROMOTE_CONNECTION_TO_USER = gql`
  mutation PromoteConnectionToUser($id: ID!) {
    promoteConnectionToUser(id: $id) {
      id
      tier
      integrationId
      status
      principalId
      createdAt
      updatedAt
    }
  }
`;

export const RECORD_ORG_USER_FALLBACK_CONSENT = gql`
  mutation RecordOrgUserFallbackConsent(
    $runId: ID!
    $approvedIntegrationIds: [String!]!
  ) {
    recordOrgUserFallbackConsent(
      runId: $runId
      approvedIntegrationIds: $approvedIntegrationIds
    )
  }
`;

// S10 — anonymous principal magic-link flow.
export const START_ANONYMOUS_RUN_SESSION = gql`
  mutation StartAnonymousRunSession($runId: ID!, $ttlSeconds: Int) {
    startAnonymousRunSession(runId: $runId, ttlSeconds: $ttlSeconds) {
      principalId
      runId
      token
      expiresAt
    }
  }
`;

export const CLAIM_ANONYMOUS_RUN = gql`
  mutation ClaimAnonymousRun($anonymousToken: String!) {
    claimAnonymousRun(anonymousToken: $anonymousToken) {
      claimedPrincipalId
      newOwnerPrincipalId
      transferredConnectionCount
    }
  }
`;

// ============================================================================
// Notifications
// ============================================================================

export const UPDATE_NOTIFICATION_PREFERENCES = gql`
  mutation UpdateNotificationPreferences(
    $channels: [String!]
    $quietHoursStart: String
    $quietHoursEnd: String
    $digestFrequency: String
  ) {
    updateNotificationPreferences(
      channels: $channels
      quietHoursStart: $quietHoursStart
      quietHoursEnd: $quietHoursEnd
      digestFrequency: $digestFrequency
    ) {
      channels
      quietHoursStart
      quietHoursEnd
      digestFrequency
      updatedAt
    }
  }
`;

export const MARK_NOTIFICATION_READ = gql`
  mutation MarkNotificationRead($notificationId: ID!) {
    markNotificationRead(notificationId: $notificationId)
  }
`;

// ============================================================================
// Person Profiles
// ============================================================================

export const ENRICH_PERSON = gql`
  mutation EnrichPerson($email: String!) {
    enrichPerson(email: $email) {
      email
      name
      phone
      company
      title
      location
      linkedInUrl
      avatarUrl
      tags
      sources
      enrichedAt
    }
  }
`;
